import { useSyncExternalStore } from 'react';

/**
 * Which listing's photograph is currently morphing between the grid and its page.
 *
 * A view transition name has to be unique in the document it is captured from, and it only produces
 * a morph when the *same* name exists on both sides. Naming every card in the results grid satisfies
 * uniqueness but not the second part: the twenty-three cars that are not being opened are captured
 * with no counterpart, and the browser then has to animate them out over the arriving page. One name
 * at a time is the only shape that describes what is actually happening — this photograph is moving.
 *
 * Kept outside React state deliberately. The value has to survive the route change that unmounts the
 * grid, so that pressing back finds the card still carrying the name and the morph runs in reverse.
 */
let target: string | null = null;
const listeners = new Set<() => void>();

export function setMorphTarget(shortCode: string | null): void {
  if (target === shortCode) {
    return;
  }

  target = shortCode;

  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): string | null {
  return target;
}

export function useMorphTarget(): string | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** The name both sides use. Derived rather than stored so the two can never drift apart. */
export function morphName(shortCode: string): string {
  return `listing-photo-${shortCode}`;
}
